import { useState, useCallback } from 'react'
import type { Network } from './types'

const STORAGE_KEY = 'ops-chain-tool-favorites'

export interface FavoriteAddress {
  address: string
  label: string
  network: Network
}

/**
 * Browser-stored favorite addresses, scoped per network.
 * Stored in localStorage as a flat array; filtered by network at read time.
 */
export function useFavorites(network: Network) {
  const [favorites, setFavorites] = useState<FavoriteAddress[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : []
    } catch {
      return []
    }
  })

  const save = (items: FavoriteAddress[]) => {
    setFavorites(items)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  }

  const forNetwork = favorites.filter((f) => f.network === network)

  const add = useCallback(
    (address: string, label: string) => {
      if (favorites.some((f) => f.address === address && f.network === network)) return
      save([...favorites, { address, label, network }])
    },
    [favorites, network]
  )

  const remove = useCallback(
    (address: string) => {
      save(favorites.filter((f) => !(f.address === address && f.network === network)))
    },
    [favorites, network]
  )

  const toggle = useCallback(
    (address: string, label?: string) => {
      const existing = favorites.find((f) => f.address === address && f.network === network)
      if (existing) {
        save(favorites.filter((f) => !(f.address === address && f.network === network)))
      } else {
        const finalLabel = label?.trim() || `${address.slice(0, 8)}…${address.slice(-4)}`
        save([...favorites, { address, label: finalLabel, network }])
      }
    },
    [favorites, network]
  )

  const updateLabel = useCallback(
    (address: string, label: string) => {
      save(
        favorites.map((f) =>
          f.address === address && f.network === network
            ? { ...f, label: label.trim() || f.label }
            : f
        )
      )
    },
    [favorites, network]
  )

  const isFavorite = useCallback(
    (address: string) => favorites.some((f) => f.address === address && f.network === network),
    [favorites, network]
  )

  return { favorites: forNetwork, add, remove, toggle, updateLabel, isFavorite }
}
